<template>
  <div id="app" class="min-h-screen">
    <nav v-if="isAuthenticated" class="bg-white shadow-sm border-b border-gray-200">
      <div class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div class="flex justify-between items-center h-16">
          <!-- Left: logo -->
          <div class="flex items-center">
            <router-link to="/" class="flex items-center gap-2 text-xl font-bold text-gray-900">
              <img src="/img/Owen-Logo-Apr24-RGB-Pos-Safe-Space-TransBG.png" alt="Owen" class="h-8" />
              <span class="text-gray-400">Hub</span>
            </router-link>
          </div>
          <!-- Right cluster: Tools dropdown → admin cog → avatar dropdown -->
          <div class="flex items-center space-x-4">
            <!-- Tools dropdown — driven by the user's granted gateway modules.
                 Hidden when the user has fewer than 2 (the gateway tile or
                 single-module auto-redirect is the entry point in that case). -->
            <div v-if="grantedModules.length >= 2" class="relative" data-tools-dropdown>
              <button
                @click="toolsDropdownOpen = !toolsDropdownOpen"
                class="text-sm text-gray-500 hover:text-indigo-600 transition-colors flex items-center gap-1"
                :class="{ 'text-indigo-600': toolsDropdownOpen }"
              >
                Tools
                <svg class="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 9l-7 7-7-7" /></svg>
              </button>
              <div v-if="toolsDropdownOpen" class="absolute top-full right-0 mt-1 w-56 bg-white border border-gray-200 rounded-lg shadow-lg z-50 py-1">
                <template v-for="mod in grantedModules" :key="mod.key">
                  <a
                    v-if="mod.type === 'external_link'"
                    :href="mod.externalUrl || '#'"
                    target="_blank"
                    rel="noopener noreferrer"
                    @click="toolsDropdownOpen = false"
                    class="flex items-center justify-between px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                    :class="{ 'opacity-50 pointer-events-none': !mod.externalUrl }"
                  >
                    {{ mod.name }}
                    <svg class="w-3 h-3 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M14 4h6v6M20 4l-8 8M19 14v5a1 1 0 0 1-1 1H5a1 1 0 0 1-1-1V6a1 1 0 0 1 1-1h5"/></svg>
                  </a>
                  <router-link
                    v-else
                    :to="mod.route"
                    @click="toolsDropdownOpen = false"
                    class="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                  >
                    {{ mod.name }}
                  </router-link>
                </template>
              </div>
            </div>
            <!-- Settings cog dropdown — admin/super only. Wraps Users, Metrics
                 and (super only) DevPanel into a single menu so the top nav
                 stays uncluttered. -->
            <div v-if="isAdmin" class="relative flex items-center" data-cog-dropdown>
              <button
                @click="cogDropdownOpen = !cogDropdownOpen"
                class="text-gray-400 hover:text-indigo-500 transition-colors"
                :class="{ 'text-indigo-500': cogDropdownOpen }"
                v-tooltip="'Admin'"
              >
                <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                </svg>
              </button>
              <div v-if="cogDropdownOpen" class="absolute right-0 top-full mt-2 w-48 bg-white border border-gray-200 rounded-lg shadow-lg z-50 py-1">
                <router-link
                  to="/admin/users"
                  @click="cogDropdownOpen = false"
                  class="flex items-center gap-2 px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                >
                  <svg class="w-4 h-4 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M17 20h5v-2a4 4 0 00-3-3.87M9 20H4v-2a4 4 0 013-3.87m6-5.13a4 4 0 11-8 0 4 4 0 018 0zm6 0a4 4 0 11-8 0 4 4 0 018 0z"/></svg>
                  Users
                </router-link>
                <router-link
                  to="/metrics"
                  @click="cogDropdownOpen = false"
                  class="flex items-center gap-2 px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                >
                  <svg class="w-4 h-4 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z"/></svg>
                  Metrics
                </router-link>
                <button
                  v-if="isSuper"
                  @click="devPanelOpen = true; cogDropdownOpen = false"
                  class="w-full text-left flex items-center gap-2 px-4 py-2 text-sm text-yellow-700 hover:bg-yellow-50 border-t border-gray-100 mt-1 pt-2"
                >
                  <svg class="w-4 h-4 text-yellow-500" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z"/><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"/></svg>
                  DevPanel
                </button>
              </div>
            </div>
            <!-- Avatar dropdown — rightmost item. Houses user identity + logout. -->
            <div class="relative flex items-center" data-avatar-dropdown>
              <button
                @click="avatarDropdownOpen = !avatarDropdownOpen"
                class="rounded-full focus:outline-none focus:ring-2 focus:ring-indigo-400 focus:ring-offset-1 transition"
                :class="{ 'ring-2 ring-indigo-400 ring-offset-1': avatarDropdownOpen }"
                aria-label="Account menu"
              >
                <img
                  v-if="user?.avatar"
                  :src="user.avatar"
                  :alt="user.name"
                  referrerpolicy="no-referrer"
                  class="w-8 h-8 rounded-full"
                />
                <div
                  v-else
                  class="w-8 h-8 rounded-full bg-gray-200 flex items-center justify-center text-sm font-medium text-gray-500"
                >
                  {{ user?.name?.charAt(0)?.toUpperCase() || '?' }}
                </div>
              </button>
              <div
                v-if="avatarDropdownOpen"
                class="absolute right-0 top-full mt-2 w-56 bg-white border border-gray-200 rounded-lg shadow-lg z-50 py-1"
              >
                <div class="px-4 py-3 border-b border-gray-100">
                  <p class="text-sm font-medium text-gray-900 truncate">{{ user?.name }}</p>
                  <p class="text-xs text-gray-500 truncate">{{ user?.email }}</p>
                </div>
                <button
                  @click="avatarDropdownOpen = false; logout()"
                  class="w-full text-left flex items-center gap-2 px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                >
                  <svg class="w-4 h-4 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
                  </svg>
                  Logout
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </nav>
    <!-- Project Setup module sub-nav. Hosts module-scoped controls (currently
         the client switcher) so the global nav stays uncluttered for users on
         other modules. Visible on any route that requires the project-setup module. -->
    <div
      v-if="isAuthenticated && $route.meta.requiresModule === 'project-setup' && hasProjectSetup"
      class="bg-gray-50 border-b border-gray-200"
    >
      <div class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex items-center h-11">
        <!-- Title group: title + client switcher, with section-specific help
             icon floating in the left margin like the in-page HelpTip pattern. -->
        <div class="relative flex items-center gap-4">
          <!-- Help dropdown — outside the content margin (left of title) so it
               reads as section-help, not a nav action. -->
          <div class="absolute -left-10 top-1/2 -translate-y-1/2 flex items-center" data-help-dropdown>
            <button
              @click="helpDropdownOpen = !helpDropdownOpen"
              class="text-gray-400 hover:text-indigo-500 transition-colors"
              :class="{ 'text-indigo-500': helpDropdownOpen }"
              v-tooltip="'Help'"
              aria-label="Section help"
            >
              <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M8.228 9c.549-1.165 2.03-2 3.772-2 2.21 0 4 1.343 4 3 0 1.4-1.278 2.575-3.006 2.907-.542.104-.994.54-.994 1.093m0 3h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </button>
            <!-- Panel opens rightward (left-0) since the icon sits in the left margin. -->
            <div v-if="helpDropdownOpen" class="absolute left-0 top-full mt-2 w-48 bg-white border border-gray-200 rounded-lg shadow-lg z-50 py-1">
              <button @click="openHelpPanel" class="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 flex items-center gap-2">
                <svg class="w-4 h-4 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" /></svg>
                Help guide
              </button>
              <button @click="replayTourFromDropdown" class="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 flex items-center gap-2">
                <svg class="w-4 h-4 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" /></svg>
                Replay guided tour
              </button>
            </div>
          </div>
          <span class="text-xs font-semibold text-gray-500 uppercase tracking-wide">Project Setup</span>
          <div v-if="clients.length > 0" class="flex items-center" data-tour="client-switcher">
            <label for="client-switcher" class="text-xs font-semibold text-gray-400 uppercase tracking-wide mr-2 whitespace-nowrap">Client</label>
            <select
              id="client-switcher"
              v-model="selectedClientCode"
              @change="onClientChange"
              class="block w-56 pl-3 pr-8 py-1 text-sm font-medium text-gray-900 bg-white border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 cursor-pointer appearance-none"
              :style="selectChevronStyle"
            >
              <option value="">All Clients</option>
              <option v-for="client in clients" :key="client.clientCode" :value="client.clientCode">
                {{ client.clientName }}
              </option>
            </select>
          </div>
        </div>
      </div>
    </div>
    <main>
      <router-view />
    </main>

    <!-- Help Panel -->
    <HelpPanel :isOpen="helpPanelOpen" @close="helpPanelOpen = false" />
    <!-- Dev Panel -->
    <DevPanel :isOpen="devPanelOpen" @close="devPanelOpen = false" />
  </div>
</template>

<script>
import { computed, ref, watch, onMounted } from 'vue'
import { useStore } from 'vuex'
import { useRouter } from 'vue-router'
import DevPanel from './components/DevPanel.vue'
import HelpPanel from './components/HelpPanel.vue'

export default {
  name: 'App',
  components: {
    DevPanel,
    HelpPanel
  },
  setup() {
    const store = useStore()
    const router = useRouter()

    const isAuthenticated = computed(() => store.getters['auth/isAuthenticated'])
    const isAdmin = computed(() => store.getters['auth/isAdmin'])
    const isSuper = computed(() => store.getters['auth/isSuper'])
    const hasProjectSetup = computed(() => store.getters['auth/hasModule']('project-setup'))
    const grantedModules = computed(() => store.getters['auth/grantedGatewayModules'])
    const user = computed(() => store.getters['auth/user'])
    const clients = computed(() => store.getters['clients/allClients'])
    const selectedClientCode = ref('')
    const devPanelOpen = ref(false)
    const helpPanelOpen = ref(false)
    const helpDropdownOpen = ref(false)
    const toolsDropdownOpen = ref(false)
    const cogDropdownOpen = ref(false)
    const avatarDropdownOpen = ref(false)

    const chevronSvg = "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 20 20'%3E%3Cpath stroke='%236b7280' stroke-linecap='round' stroke-linejoin='round' stroke-width='1.5' d='M6 8l4 4 4-4'/%3E%3C/svg%3E"
    const selectChevronStyle = {
      backgroundImage: `url("${chevronSvg}")`,
      backgroundPosition: 'right 0.5rem center',
      backgroundRepeat: 'no-repeat',
      backgroundSize: '1.25em 1.25em'
    }

    const logout = async () => {
      await store.dispatch('auth/logout')
      router.push('/login')
    }

    const onClientChange = () => {
      store.dispatch('clients/selectClient', selectedClientCode.value)
      // Client switcher only matters inside Project Setup — nav back there.
      if (router.currentRoute.value.path !== '/project-setup') {
        router.push('/project-setup')
      }
    }

    const replayTour = () => {
      // Clear all tour completion flags and reload to restart
      Object.keys(localStorage).forEach(key => {
        if (key.startsWith('owen-tour-')) localStorage.removeItem(key)
      })
      window.location.reload()
    }

    const openHelpPanel = () => {
      helpDropdownOpen.value = false
      helpPanelOpen.value = true
    }

    const replayTourFromDropdown = () => {
      helpDropdownOpen.value = false
      replayTour()
    }

    // Close dropdowns on outside click
    const onDocClick = (e) => {
      if (helpDropdownOpen.value && !e.target.closest('[data-help-dropdown]')) {
        helpDropdownOpen.value = false
      }
      if (toolsDropdownOpen.value && !e.target.closest('[data-tools-dropdown]')) {
        toolsDropdownOpen.value = false
      }
      if (cogDropdownOpen.value && !e.target.closest('[data-cog-dropdown]')) {
        cogDropdownOpen.value = false
      }
      if (avatarDropdownOpen.value && !e.target.closest('[data-avatar-dropdown]')) {
        avatarDropdownOpen.value = false
      }
    }
    onMounted(() => document.addEventListener('click', onDocClick))
    // Clean up on unmount not needed — App.vue lives for the lifetime of the app

    // Hub clients are project-setup data — fetching them 403s for users without
    // that module. Skip the call rather than letting the interceptor bounce them
    // to the gateway with a stale "module not granted" notice.
    const loadClientsIfPermitted = async () => {
      if (!isAuthenticated.value || !hasProjectSetup.value) return
      await store.dispatch('clients/fetchClients')
      const storedCode = store.getters['clients/selectedClientCode']
      if (storedCode) selectedClientCode.value = storedCode
    }

    onMounted(loadClientsIfPermitted)
    watch(isAuthenticated, loadClientsIfPermitted)
    watch(hasProjectSetup, loadClientsIfPermitted)

    return {
      isAuthenticated,
      isAdmin,
      isSuper,
      hasProjectSetup,
      grantedModules,
      user,
      clients,
      selectedClientCode,
      selectChevronStyle,
      devPanelOpen,
      helpPanelOpen,
      helpDropdownOpen,
      toolsDropdownOpen,
      cogDropdownOpen,
      avatarDropdownOpen,
      logout,
      onClientChange,
      openHelpPanel,
      replayTourFromDropdown
    }
  }
}
</script>
